import { proxy, transfer, wrap } from 'comlink';
import type { AnalyzeOutput } from '@/core/analysis/pipeline';
import type { AnalysisProgress } from '@/core/analysis/types';
import type { AnalysisWorkerApi, ChordModel } from './analysis.worker';

export type { ChordModel };

export class AnalysisCancelledError extends Error {
  constructor() {
    super('Analysis cancelled');
    this.name = 'AnalysisCancelledError';
  }
}

export interface AnalysisJob {
  result: Promise<AnalyzeOutput>;
  /** Stops the analysis immediately; `result` rejects with AnalysisCancelledError. */
  cancel(): void;
}

export interface AnalysisJobOptions {
  onProgress?: (progress: AnalysisProgress) => void;
  debug?: boolean;
  /** 'btc': the BTC model finds the chords (falls back to the engine if it can't load). */
  model?: ChordModel;
}

/**
 * Runs one analysis in a fresh Web Worker (F2.12). `samples` (mono, usually 22 050 Hz) is
 * transferred, not copied, so the caller must not use it afterwards. Cancelling terminates the
 * worker, which is immediate and leaves nothing behind.
 */
export function startAnalysis(
  samples: Float32Array,
  sampleRate: number,
  options: AnalysisJobOptions = {},
): AnalysisJob {
  const worker = new Worker(new URL('./analysis.worker.ts', import.meta.url), {
    type: 'module',
    name: 'analysis',
  });
  const remote = wrap<AnalysisWorkerApi>(worker);
  let rejectCancelled: ((e: Error) => void) | null = null;
  let finished = false;

  const cancelled = new Promise<never>((_, reject) => {
    rejectCancelled = reject;
  });
  const crashed = new Promise<never>((_, reject) => {
    worker.addEventListener('error', (e) => {
      reject(new Error(`Analysis worker failed: ${e.message}`));
    });
  });
  const work = remote.analyze(
    transfer(samples, [samples.buffer]),
    sampleRate,
    proxy((p: AnalysisProgress) => options.onProgress?.(p)),
    options.debug ?? false,
    options.model ?? 'dsp',
  );

  const result = Promise.race([work, cancelled, crashed]).finally(() => {
    finished = true;
    worker.terminate();
  });

  return {
    result,
    cancel() {
      if (finished) return;
      worker.terminate();
      rejectCancelled?.(new AnalysisCancelledError());
    },
  };
}
